// WHAT: Dialog component for manually entering time tracking entries
// WHY:  Provides comprehensive form for entering time entries with all tracking fields
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: Dialog from headlessui/react, Button component
// RISKS:
//   - NOTE: Form validation may be incomplete - may allow invalid data
//   - NOTE: Save handler is passed as prop - depends on parent implementation
//   - NOTE: Many form fields - may be overwhelming for users

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { type FC } from "react";

import { Button } from "@/components/ui";

/**
 * Props for ManualEntryDialog component
 */
interface ManualEntryDialogProperties {
  aspectOptions: string[];
  duration: string;
  efficiencyRating: null | number;
  impactScore: string;
  notes: string;
  onAspectChange: (value: string) => void;
  onClose: () => void;
  onDurationChange: (value: string) => void;
  onEfficiencyRatingChange: (value: null | number) => void;
  onImpactScoreChange: (value: string) => void;
  onNotesChange: (value: string) => void;
  onSave: () => void;
  onStartChange: (value: string) => void;
  onWhatWasInefficientChange: (value: string) => void;
  onWhatWentWellChange: (value: string) => void;
  onWorkspaceChange: (value: string) => void;
  open: boolean;
  selectedAspect: string;
  selectedWorkspace: string;
  start: string;
  whatWasInefficient: string;
  whatWentWell: string;
  workspaceOptions: string[];
}
/**
 * WHAT:
 *   Dialog component that provides comprehensive form for manually entering
 *   time tracking entries with all optional fields.
 *
 * WHY:
 *   Enables users to retroactively add time entries with full tracking data
 *   including efficiency ratings and reflection notes.
 *
 * PROPS:
 *   - open: boolean — Dialog visibility
 *   - onClose: () => void — Close handler
 *   - workspaceOptions: string[] — Available workspace options
 *   - selectedWorkspace: string — Selected workspace
 *   - onWorkspaceChange: (value: string) => void — Workspace change handler
 *   - aspectOptions: string[] — Available aspect options (depends on workspace)
 *   - selectedAspect: string — Selected aspect
 *   - onAspectChange: (value: string) => void — Aspect change handler
 *   - start: string — Start time (datetime-local format)
 *   - onStartChange: (value: string) => void — Start time change handler
 *   - duration: string — Duration input
 *   - onDurationChange: (value: string) => void — Duration change handler
 *   - notes: string — Notes text
 *   - onNotesChange: (value: string) => void — Notes change handler
 *   - efficiencyRating: number | null — Efficiency rating (1-5)
 *   - onEfficiencyRatingChange: (value: number | null) => void — Rating change handler
 *   - whatWentWell: string — What went well text
 *   - onWhatWentWellChange: (value: string) => void — What went well change handler
 *   - whatWasInefficient: string — What was inefficient text
 *   - onWhatWasInefficientChange: (value: string) => void — Inefficient change handler
 *   - impactScore: string — Impact score
 *   - onImpactScoreChange: (value: string) => void — Impact score change handler
 *   - onSave: () => void — Save handler
 *
 * BEHAVIOR:
 *   - Displays modal dialog with form fields
 *   - Workspace selection enables aspect selection
 *   - Collects all tracking data including optional fields
 *   - Provides save and cancel actions
 *
 * RISKS:
 *   - NOTE: Form validation may be incomplete - may allow invalid data
 *   - NOTE: Save handler is passed as prop - depends on parent implementation
 *   - NOTE: Many form fields - may be overwhelming for users
 *
 * @param root0
 *
 * @param root0.open
 *
 * @param root0.onClose
 *
 * @param root0.workspaceOptions
 *
 * @param root0.selectedWorkspace
 *
 * @param root0.onWorkspaceChange
 *
 * @param root0.aspectOptions
 *
 * @param root0.selectedAspect
 *
 * @param root0.onAspectChange
 *
 * @param root0.start
 *
 * @param root0.onStartChange
 *
 * @param root0.duration
 *
 * @param root0.onDurationChange
 *
 * @param root0.notes
 *
 * @param root0.onNotesChange
 *
 * @param root0.efficiencyRating
 *
 * @param root0.onEfficiencyRatingChange
 *
 * @param root0.whatWentWell
 *
 * @param root0.onWhatWentWellChange
 *
 * @param root0.whatWasInefficient
 *
 * @param root0.onWhatWasInefficientChange
 *
 * @param root0.impactScore
 *
 * @param root0.onImpactScoreChange
 *
 * @param root0.onSave
 *
 * @returns {JSX.Element | null} The manual entry dialog component or null if not open
 *
 */
const ManualEntryDialog: FC<ManualEntryDialogProperties> = ({
  aspectOptions,
  duration,
  efficiencyRating,
  impactScore,
  notes,
  onAspectChange,
  onClose,
  onDurationChange,
  onEfficiencyRatingChange,
  onImpactScoreChange,
  onNotesChange,
  onSave,
  onStartChange,
  onWhatWasInefficientChange,
  onWhatWentWellChange,
  onWorkspaceChange,
  open,
  selectedAspect,
  selectedWorkspace,
  start,
  whatWasInefficient,
  whatWentWell,
  workspaceOptions,
}) => {
  if (!open) {
    return null;
  }

  return (
    <Dialog className="relative z-50" onClose={onClose} open={open} static>
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-slate-800/95 border border-slate-600/50 text-white rounded-2xl p-8 shadow-tracq-card w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100">
          <DialogTitle className="text-2xl font-semibold text-tracq-bright-blue mb-6">
            Manueller Eintrag
          </DialogTitle>

          <div className="space-y-6">
            {/* Workspace */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Workspace</label>
              <select
                className="w-full bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  onWorkspaceChange(e.target.value);
                }}
                value={selectedWorkspace}
              >
                <option value="">-- Workspace wählen --</option>
                {workspaceOptions.map((ws) => (
                  <option key={ws} value={ws}>
                    {ws}
                  </option>
                ))}
              </select>
            </div>

            {/* Aspect */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Aspect</label>
              <select
                className="w-full bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!selectedWorkspace}
                onChange={(e) => {
                  onAspectChange(e.target.value);
                }}
                value={selectedAspect}
              >
                <option value="">-- Aspect wählen --</option>
                {aspectOptions.map((asp) => (
                  <option key={asp} value={asp}>
                    {asp}
                  </option>
                ))}
              </select>
            </div>

            {/* Start Time */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Startzeit</label>
              <input
                className="w-full bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  onStartChange(e.target.value);
                }}
                type="datetime-local"
                value={start}
              />
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Dauer (Minuten)
              </label>
              <input
                className="w-full bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  onDurationChange(e.target.value);
                }}
                placeholder="Dauer in Minuten"
                type="number"
                value={duration}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Notizen</label>
              <textarea
                className="w-full h-24 bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  onNotesChange(e.target.value);
                }}
                placeholder="Was hast du gemacht?"
                value={notes}
              />
            </div>

            {/* Efficiency Rating */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Wie effizient war die Stunde? (1-10)
              </label>
              <div className="flex items-center gap-4">
                <input
                  className="flex-1 h-2 bg-[#212B36] rounded-lg appearance-none cursor-pointer accent-tracq-bright-blue"
                  max="10"
                  min="1"
                  onChange={(e) => {
                    onEfficiencyRatingChange(Number.parseInt(e.target.value));
                  }}
                  type="range"
                  value={efficiencyRating ?? 5}
                />
                <div className="w-12 text-center text-lg font-semibold text-tracq-bright-blue">
                  {efficiencyRating ?? 5}
                </div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>1 - Sehr ineffizient</span>
                <span>10 - Sehr effizient</span>
              </div>
            </div>

            {/* What went well */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Was lief gut?</label>
              <textarea
                className="w-full h-32 bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  onWhatWentWellChange(e.target.value);
                }}
                placeholder="Beschreibe, was während dieser Stunde gut gelaufen ist..."
                value={whatWentWell}
              />
            </div>

            {/* What was inefficient */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Was war ineffizient?
              </label>
              <textarea
                className="w-full h-32 bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  onWhatWasInefficientChange(e.target.value);
                }}
                placeholder="Beschreibe, was während dieser Stunde ineffizient war..."
                value={whatWasInefficient}
              />
            </div>

            {/* Impact Score */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Impact Score</label>
              <select
                className="w-full bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  onImpactScoreChange(e.target.value);
                }}
                value={impactScore}
              >
                <option value="">Keine Auswahl</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">Wie hoch ist der Impact dieser Arbeit?</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-[#334155] mt-6">
            <Button onClick={onClose} variant="secondary">
              Abbrechen
            </Button>
            <Button onClick={onSave} variant="primary">
              Speichern
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export { ManualEntryDialog };
